export type CustomerKind = 'company' | 'person';
export type CustomerStatus = 'active' | 'blocked' | 'prospect';
export type CustomerAddressKind = 'billing' | 'delivery' | 'site';
export type CustomerDuplicateReason = 'vat-id' | 'email' | 'phone' | 'name-postal-code' | 'name-city' | 'name';

export interface CustomerAddressRecord {
  readonly id: string;
  readonly kind: CustomerAddressKind;
  readonly label: string;
  readonly street: string;
  readonly postalCode: string;
  readonly city: string;
  readonly countryCode: string;
  readonly primary: boolean;
}

export interface CustomerContactRecord {
  readonly id: string;
  readonly salutation: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly position: string;
  readonly email: string;
  readonly phone: string;
  readonly mobile: string;
  readonly primary: boolean;
  readonly active: boolean;
}

export interface CustomerSpecialPart {
  readonly id: string;
  readonly partNumber: string;
  readonly description: string;
  readonly note: string;
}

/** Ein mit dem Kunden verknüpfter Vorgang (Auftrag/Service/Miete/Angebot) -- für den Vorgänge-Tab. */
export interface CustomerOperation {
  readonly kind: 'order' | 'service' | 'rental' | 'offer';
  readonly number: string;
  readonly title: string;
  readonly status: string;
  readonly date: string;
}

/** Eine Rechnung des Kunden (leichtgewichtig) -- für Umsatz/OPOS im Rechnungen-Tab. */
export interface CustomerInvoiceLine {
  readonly number: string;
  readonly date: string;
  readonly gross: number;
  readonly open: number;
  readonly status: string;
}

/** Kennzahlen + Rechnungsliste je Kunde (Umsatz, offene Posten). */
export interface CustomerFinancials {
  readonly revenue: number;
  readonly openReceivables: number;
  readonly invoices: readonly CustomerInvoiceLine[];
}

/** Konditionen & Buchhaltung: Debitorennummer, Kreditlimit, Zahlungsziel, Rabatt, Bank, Freitext-Konditionen
 * und Sonder-Ersatzteile. (Feldname bleibt „agreements" für Kompatibilität.) */
export interface CustomerAgreements {
  /** Debitorennummer für die Buchhaltung/DATEV (frei; leer = noch keine). */
  readonly debtorNumber: string;
  /**
   * Leitweg-ID (BT-10) für Rechnungen an die öffentliche Hand. Der Auftraggeber gibt sie vor; sie
   * adressiert die Rechnung im Behördennetz. Leer = normaler B2B-Kunde, dann trägt BT-10 wie bisher
   * die Vorgangsnummer. Aufbau: Grobadressierung-Feinadressierung-Prüfziffer, z. B. 991-01234-56.
   */
  readonly leitwegId: string;
  /**
   * Mahnsperre: dieser Kunde wird nicht gemahnt. Gibt es, weil eine strittige oder in Klaerung
   * befindliche Forderung sonst automatisch die naechste Mahnstufe bekommt -- und ein Mahnbrief
   * an den falschen Kunden kostet mehr als die Forderung.
   */
  readonly dunningBlocked: boolean;
  /** Warum gesperrt -- steht im Mahnlauf statt des Knopfes. */
  readonly dunningBlockReason: string;
  /** Kreditlimit in EUR (0 = keins hinterlegt). */
  readonly creditLimit: number;
  readonly discountPercent: number;
  readonly paymentTermDays: number;
  readonly conditions: string;
  /** Lieferbedingungen lt. Incoterms (frei, z. B. „EXW Werl" / „DAP frei Haus"). */
  readonly deliveryTerms: string;
  readonly bankName: string;
  readonly iban: string;
  readonly bic: string;
  readonly specialParts: readonly CustomerSpecialPart[];
}

export interface CustomerListRecord {
  readonly id: string;
  readonly number: string;
  readonly displayName: string;
  readonly city: string;
  readonly status: CustomerStatus;
  readonly phone: string;
  readonly email: string;
  readonly vatId: string;
  readonly contactCount: number;
  readonly updatedAt: string;
  /** Zugang: seit wann der Kunde im Bestand ist (ISO-Datum). */
  readonly activeSince: string;
  /** Abgang: seit wann nicht mehr im Bestand (ISO-Datum) oder null, wenn noch aktiv. */
  readonly activeUntil: string | null;
}

export interface CustomerDetailRecord extends CustomerListRecord {
  readonly tenantId: string;
  readonly kind: CustomerKind;
  readonly companyName: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly taxNumber: string;
  readonly website: string;
  readonly notes: string;
  readonly addresses: readonly CustomerAddressRecord[];
  readonly contacts: readonly CustomerContactRecord[];
  readonly agreements: CustomerAgreements;
  readonly createdAt: string;
}

export interface CustomerSearchInput {
  readonly tenantId: string;
  readonly query?: string;
  readonly status?: CustomerStatus | 'all';
  /** Bestand-im-Zeitraum: nur Kunden, die zwischen diesen Daten im Bestand waren. */
  readonly bestandFrom?: string;
  readonly bestandTo?: string;
  readonly page?: number;
  readonly pageSize?: number;
}

export interface NormalizedCustomerSearchInput {
  readonly tenantId: string;
  readonly query: string;
  readonly status: CustomerStatus | 'all';
  readonly bestandFrom: string;
  readonly bestandTo: string;
  readonly page: number;
  readonly pageSize: number;
}

export interface CustomerPage {
  readonly items: readonly CustomerListRecord[];
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
}

export interface CustomerAddressInput {
  readonly label?: string;
  readonly street: string;
  readonly postalCode: string;
  readonly city: string;
  readonly countryCode?: string;
}

export interface CustomerContactInput {
  readonly salutation?: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly position?: string;
  readonly email?: string;
  readonly phone?: string;
  readonly mobile?: string;
  readonly primary?: boolean;
}

export interface CreateCustomerInput {
  readonly tenantId: string;
  readonly kind: CustomerKind;
  readonly companyName?: string;
  readonly firstName?: string;
  readonly lastName?: string;
  readonly vatId?: string;
  readonly taxNumber?: string;
  readonly email?: string;
  readonly phone?: string;
  readonly website?: string;
  readonly notes?: string;
  readonly billingAddress?: CustomerAddressInput;
  readonly primaryContact?: CustomerContactInput;
  readonly agreements?: CustomerAgreements;
}

export interface UpdateCustomerInput extends Omit<CreateCustomerInput, 'tenantId' | 'billingAddress' | 'primaryContact'> {
  readonly tenantId: string;
  readonly customerId: string;
  readonly status?: CustomerStatus;
}

export interface CreateCustomerContactInput extends CustomerContactInput {
  readonly tenantId: string;
  readonly customerId: string;
}

export interface UpdateCustomerContactInput extends CustomerContactInput {
  readonly tenantId: string;
  readonly customerId: string;
  readonly contactId: string;
  readonly active?: boolean;
}

export interface CreateCustomerAddressInput extends CustomerAddressInput {
  readonly tenantId: string;
  readonly customerId: string;
  readonly kind: CustomerAddressKind;
  readonly primary?: boolean;
}

export interface UpdateCustomerAddressInput extends CreateCustomerAddressInput {
  readonly addressId: string;
}

export interface CheckCustomerDuplicatesInput {
  readonly tenantId: string;
  readonly customerId?: string;
  readonly kind: CustomerKind;
  readonly companyName?: string;
  readonly firstName?: string;
  readonly lastName?: string;
  readonly vatId?: string;
  readonly email?: string;
  readonly phone?: string;
  readonly postalCode?: string;
  readonly city?: string;
}

export interface CustomerDuplicateCandidate {
  readonly id: string;
  readonly number: string;
  readonly displayName: string;
  readonly city: string;
  readonly status: CustomerStatus;
  readonly score: number;
  readonly reasons: readonly CustomerDuplicateReason[];
}

export interface UpdateCustomerAgreementsInput {
  readonly tenantId: string;
  readonly customerId: string;
  readonly agreements: CustomerAgreements;
}

export type CustomerAttachmentKind = 'image' | 'document';

/** Ein Dokument/Bild an der Kundenakte (z. B. Verträge, Schriftverkehr, gescannte Unterlagen).
 *  content ist eine data:-URL; in Listen ist er bei Dokumenten leer und wird beim Download einzeln
 *  über getAttachment nachgeladen (dasselbe Muster wie die Maschinen-Anhänge). */
export interface CustomerAttachmentRecord {
  readonly id: string;
  readonly kind: CustomerAttachmentKind;
  readonly title: string;
  readonly mimeType: string;
  readonly byteSize: number;
  readonly createdAt: string;
  readonly content: string;
}

export interface AddCustomerAttachmentInput {
  readonly tenantId: string;
  readonly customerId: string;
  readonly kind: CustomerAttachmentKind;
  readonly title: string;
  readonly mimeType: string;
  readonly byteSize: number;
  readonly content: string;
}

export interface CustomersGateway {
  searchCustomers(input: CustomerSearchInput): Promise<CustomerPage>;
  getCustomer(tenantId: string, customerId: string): Promise<CustomerDetailRecord>;
  findDuplicates(input: CheckCustomerDuplicatesInput): Promise<readonly CustomerDuplicateCandidate[]>;
  createCustomer(input: CreateCustomerInput): Promise<CustomerDetailRecord>;
  updateCustomer(input: UpdateCustomerInput): Promise<CustomerDetailRecord>;
  createAddress(input: CreateCustomerAddressInput): Promise<CustomerDetailRecord>;
  updateAddress(input: UpdateCustomerAddressInput): Promise<CustomerDetailRecord>;
  deleteAddress(tenantId: string, customerId: string, addressId: string): Promise<CustomerDetailRecord>;
  createContact(input: CreateCustomerContactInput): Promise<CustomerDetailRecord>;
  updateContact(input: UpdateCustomerContactInput): Promise<CustomerDetailRecord>;
  updateAgreements(input: UpdateCustomerAgreementsInput): Promise<CustomerDetailRecord>;
  /** Kundenakte: Dokumente/Bilder hinterlegen (Verträge, Schriftverkehr, gescannte Unterlagen). */
  listAttachments(tenantId: string, customerId: string): Promise<readonly CustomerAttachmentRecord[]>;
  getAttachment(tenantId: string, attachmentId: string): Promise<CustomerAttachmentRecord>;
  addAttachment(input: AddCustomerAttachmentInput): Promise<CustomerAttachmentRecord>;
  removeAttachment(tenantId: string, attachmentId: string): Promise<void>;
}

export function normalizeCustomerSearchInput(input: CustomerSearchInput): NormalizedCustomerSearchInput {
  return {
    tenantId: input.tenantId,
    query: input.query?.trim() ?? '',
    status: input.status ?? 'all',
    bestandFrom: input.bestandFrom?.trim() ?? '',
    bestandTo: input.bestandTo?.trim() ?? '',
    page: Math.max(1, Math.trunc(input.page ?? 1)),
    pageSize: Math.min(100, Math.max(1, Math.trunc(input.pageSize ?? 25))),
  };
}

export function emptyCustomerAgreements(): CustomerAgreements {
  return { debtorNumber: '', leitwegId: '', dunningBlocked: false, dunningBlockReason: '', creditLimit: 0, discountPercent: 0, paymentTermDays: 30, conditions: '', deliveryTerms: '', bankName: '', iban: '', bic: '', specialParts: [] };
}

export function normalizeCustomerAgreements(input: CustomerAgreements | undefined): CustomerAgreements {
  if (!input) return emptyCustomerAgreements();
  const discountPercent = Math.min(100, Math.max(0, Math.round((Number(input.discountPercent) || 0) * 100) / 100));
  const paymentTermDays = Math.max(0, Math.trunc(Number(input.paymentTermDays) || 0));
  const creditLimit = Math.max(0, Math.round((Number(input.creditLimit) || 0) * 100) / 100);
  const specialParts = (input.specialParts ?? [])
    .map((part, index) => ({
      id: part.id || `part-${index + 1}`,
      partNumber: (part.partNumber ?? '').trim(),
      description: (part.description ?? '').trim(),
      note: (part.note ?? '').trim(),
    }))
    .filter((part) => part.partNumber || part.description);
  return {
    debtorNumber: (input.debtorNumber ?? '').trim(),
    leitwegId: (input.leitwegId ?? '').trim(),
    dunningBlocked: input.dunningBlocked === true,
    dunningBlockReason: (input.dunningBlockReason ?? '').trim(),
    creditLimit,
    discountPercent,
    paymentTermDays,
    conditions: (input.conditions ?? '').trim(),
    deliveryTerms: (input.deliveryTerms ?? '').trim(),
    bankName: (input.bankName ?? '').trim(),
    iban: (input.iban ?? '').replace(/\s+/g, '').toUpperCase(),
    bic: (input.bic ?? '').trim().toUpperCase(),
    specialParts,
  };
}
